/**
 * Sprint 11.2d — Resume Enrichment queue.
 *
 * The dedicated enrichment team's home page: lists candidates whose AI-generated
 * templated resume needs a human pass. Click a row → existing candidate detail
 * page (Company Resume tab) to edit + approve.
 *
 * Backend: GET /api/v1/resume-enrichment/queue?status=&q=&page=&page_size=
 * Status pill driven by `edited_at`: NULL = "Needs review" / NOT NULL = "Approved".
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { v1Get } from '@/lib/api';
import { fmtDateTimeIST } from '@/lib/format';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Tabs, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar } from '@/components/Avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Search, ChevronRight, Sparkles, CheckCircle2, Clock } from 'lucide-react';
import {
  DataTablePagination, usePagedQueryParams,
} from '@/components/DataTablePagination';

const STATUS_PILL = {
  needs_review: { tone: 'amber', icon: Clock, label: 'Needs review' },
  approved:     { tone: 'emerald', icon: CheckCircle2, label: 'Approved' },
};

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return fmtDateTimeIST(iso, { dateStyle: 'medium', timeStyle: 'short' }, iso);
  } catch { return iso; }
};

export default function ResumeEnrichment() {
  const navigate = useNavigate();
  const { params, setPage, setPageSize, setFilter } = usePagedQueryParams({
    initial: { q: '', status: 'needs_review' },
    // `all` is a real queue filter sent to the API; do not strip it from the URL.
    stripAllFilters: false,
  });
  const [searchInput, setSearchInput] = useState(() => params.q || '');

  React.useEffect(() => {
    const t = setTimeout(() => {
      if ((params.q || '') !== searchInput) setFilter('q', searchInput);
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const qs = new URLSearchParams();
  qs.set('page', params.page);
  qs.set('page_size', params.page_size);
  qs.set('status', params.status || 'needs_review');
  if (params.q) qs.set('q', params.q);

  const { data, isFetching } = useQuery({
    queryKey: ['v1-enrichment-queue', qs.toString()],
    queryFn: () => v1Get(`/resume-enrichment/queue?${qs.toString()}`),
    keepPreviousData: true,
    refetchInterval: 60_000,
  });
  const items = data?.items || [];
  const total = data?.total ?? 0;
  const totalPages = data?.total_pages ?? 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Resume Enrichment"
        subtitle="Curate AI-generated templated resumes — the heart of the HireIQ output."
      />

      <Tabs value={params.status} onValueChange={(v) => setFilter('status', v)}>
        <TabsList>
          <TabsTrigger value="needs_review" data-testid="enrich-tab-needs-review">
            Needs review
          </TabsTrigger>
          <TabsTrigger value="approved" data-testid="enrich-tab-approved">
            Approved
          </TabsTrigger>
          <TabsTrigger value="all" data-testid="enrich-tab-all">All</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card className="card-elevated">
        <CardContent className="p-3 md:p-4">
          <div className="relative w-full md:max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by name or email"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9"
              data-testid="enrich-search-input"
            />
          </div>
        </CardContent>
      </Card>

      <Card className="card-elevated overflow-hidden">
        <Table data-testid="enrich-table">
          <TableHeader>
            <TableRow className="bg-secondary/60">
              <TableHead>Candidate</TableHead>
              <TableHead>Role / Department</TableHead>
              <TableHead>Timestamp</TableHead>
              <TableHead>Last edited</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isFetching && items.length === 0 && (
              <>
                {Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    {Array.from({ length: 6 }).map((__, j) => (
                      <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))}
              </>
            )}
            {!isFetching && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">
                  {params.status === 'needs_review'
                    ? 'No resumes awaiting review. Great work!'
                    : params.status === 'approved'
                      ? 'No approved resumes yet.'
                      : 'No candidates with a generated company resume yet.'}
                </TableCell>
              </TableRow>
            )}
            {items.map((row) => {
              const pillSpec = STATUS_PILL[row.status] || STATUS_PILL.needs_review;
              const Icon = pillSpec.icon;
              const enrichPath = row.evaluation_id
                ? `/portal/resume-enrichment/${row.candidate_id}?evaluation_id=${encodeURIComponent(row.evaluation_id)}`
                : `/portal/resume-enrichment/${row.candidate_id}`;
              return (
                <TableRow
                  key={row.candidate_id}
                  data-testid="enrich-row"
                  className="cursor-pointer"
                  onClick={() => navigate(enrichPath)}
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar name={row.full_name} size={32} src={row.photo_url} />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold">{row.full_name}</p>
                        <p className="truncate text-xs text-muted-foreground">{row.email || '—'}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <p className="text-sm">{row.role_label || '—'}</p>
                    <p className="text-xs text-muted-foreground">{row.department_label || '—'}</p>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <div>
                        <p className="text-xs tabular-nums text-muted-foreground">
                          {fmtDateTime(row.generated_at)}
                        </p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <p className="text-xs tabular-nums text-muted-foreground">
                      {fmtDateTime(row.edited_at)}
                    </p>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={`gap-1 ${
                        pillSpec.tone === 'emerald'
                          ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                          : 'border-amber-200 bg-amber-50 text-amber-800'
                      }`}
                    >
                      <Icon className="h-3 w-3" /> {pillSpec.label}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost" size="sm" className="gap-1 pr-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(enrichPath);
                      }}
                      data-testid="enrich-open-button"
                    >
                      Enrich <ChevronRight className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <DataTablePagination
          page={params.page}
          pageSize={params.page_size}
          total={total}
          totalPages={totalPages}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          isLoading={isFetching}
        />
      </Card>
    </div>
  );
}
